import { HttpClient, APIResponseType } from './types';
import { AxiosAdapter } from './AxiosAdapter'; // or FetchAdapter
import { QueryClient, isCancelledError } from '@tanstack/react-query';
import queryClient from './queryClient';

class ApiService {
  private httpClient: HttpClient;
  private queryClient: QueryClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
    this.queryClient = queryClient;
  }

  // Helper method to generate query key
  private getQueryKey(method: string, url: string, params?: Record<string, any>): any[] {
    return [method, url, params ? { ...params } : {}];
  }

  /**
   * Wraps a React-Query `fetchQuery` promise to silently absorb the
   * `CancelledError` raised when `invalidateCacheByPrefix` (or any other
   * `removeQueries` call) drops an in-flight query. Without this guard those
   * cancellations bubble up as unhandled promise rejections and surface as
   * `[object Object]` runtime errors in the CRA dev overlay.
   */
  private async awaitWithCancellationGuard<T>(
    promise: Promise<APIResponseType<T>>,
  ): Promise<APIResponseType<T>> {
    promise.catch(() => {});
    try {
      return await promise;
    } catch (error) {
      if (isCancelledError(error) || (error as any)?.silent === true) {
        const cancelled = new Error('Request cancelled') as Error & {
          isCancelled?: boolean;
          silent?: boolean;
        };
        cancelled.isCancelled = true;
        cancelled.silent = true;
        throw cancelled;
      }
      throw error;
    }
  }

  // Generic method to make GET requests with caching
  async get<T>(url: string, params?: Record<string, any>): Promise<APIResponseType<T>> {
    // Check if this is a blob request and disable caching
    if (params?.responseType === 'blob') {
      return this.httpClient.get<T>(url, params);
    }

    const queryKey = this.getQueryKey('GET', url, params);

    // Return cached data if available, otherwise fetch fresh data
    return this.awaitWithCancellationGuard(
      this.queryClient.fetchQuery({
        queryKey,
        queryFn: () => this.httpClient.get<T>(url, params),
      }),
    );
  }

  // Generic method to make POST requests (with optional caching)
  async post<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    console.log("ApiService post method called with URL:", url);
    console.log("POST data:", data);

    if (enableCache) {
      const queryKey = this.getQueryKey('POST', url, { data, ...config });
      return this.awaitWithCancellationGuard(
        this.queryClient.fetchQuery({
          queryKey,
          queryFn: () => {
            console.log("Executing POST request via queryFn");
            return this.httpClient.post<T>(url, data, config);
          },
        }),
      );
    }
    return this.httpClient.post<T>(url, data, config);
  }

  // Generic method to make PUT requests (with optional caching)
  async put<T>(url: string, data?: any, config?: any, enableCache = true): Promise<APIResponseType<T>> {
    if (enableCache) {
      const queryKey = this.getQueryKey('PUT', url, { data, ...config });
      return this.awaitWithCancellationGuard(
        this.queryClient.fetchQuery({
          queryKey,
          queryFn: () => this.httpClient.put<T>(url, data, config),
        }),
      );
    }
    return this.httpClient.put<T>(url, data, config);
  }

  // Generic method to make DELETE requests
  async delete<T>(url: string, config?: any): Promise<APIResponseType<T>> {
    // Typically we don't cache DELETE requests
    return this.httpClient.delete<T>(url, config);
  }

  /**
   * Marks the cached entry for a specific endpoint as stale and refetches
   * active observers. We use `invalidateQueries` rather than `removeQueries`
   * so that in-flight requests are *not* aborted — aborting them raises a
   * React-Query `CancelledError` that bubbles up as an unhandled promise
   * rejection (showing `[object Object]` in the CRA dev overlay).
   */
  invalidateCache(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url, params);

    console.log("🔄 Invalidating Cache for:", queryKey);

    void this.queryClient.invalidateQueries({ queryKey });
  }

  /**
   * Prefix variant of {@link invalidateCache}. Matches all queries whose key
   * starts with the given (`method`, `url`) tuple so a whole endpoint family
   * can be refreshed without aborting any of their in-flight calls.
   */
  invalidateCacheByPrefix(url: string, method: string = 'GET', params?: Record<string, any>) {
    const queryKey = this.getQueryKey(method, url);
    console.log("🔄 Invalidating Cache by prefix for:", queryKey);
    void this.queryClient.invalidateQueries({
      queryKey,
      exact: false, // important for partial match
    });
  }

  // Method to clear entire cache
  // clearCache() {
  //   this.queryClient.clear();
  // }
}

// Create an instance of ApiService with the desired HTTP client
const httpClient = new AxiosAdapter(); // or new FetchAdapter()
const apiService = new ApiService(httpClient);

// Add S3 content fetching service
export const fetchS3ContentThroughBackend = async (s3Url: string): Promise<{ content: string; contentType?: string; }> => {
  try {
    const response = await httpClient.post<{ content: string; contentType?: string; }>('api/fetch-s3-content', {
      url: s3Url,
    });

    if (response.body) {
      return response.body;
    } else {
      throw new Error(response.message || 'Failed to fetch S3 content');
    }
  } catch (error) {
    console.error('Error fetching S3 content through backend:', error);
    throw error;
  }
};

export default apiService;